import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";
import Swal from "sweetalert2";

const RolesList = ({ roles = [], onEdit, onDelete, onAddRole }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(filterText.toLowerCase()) ||
      (role.permissions &&
        role.permissions.some((p) =>
          p.name.toLowerCase().includes(filterText.toLowerCase())
        ))
  );

  const totalRows = filteredRoles.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);

    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        {/* Left: Rows per page - With original dropdown styling */}
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px", fontSize: "0.75rem" }}
            value={rowsPerPage}
            onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
          >
            {[5, 10, 15, 20, 25].map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  const customStyles = {
    pagination: {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderTop: "1px solid #dee2e6",
        padding: "10px 20px",
      },
      pageButtonsStyle: {
        borderRadius: "6px",
        height: "32px",
        padding: "0 10px",
        margin: "0 4px",
        cursor: "pointer",
        color: "#495057",
        border: "1px solid #dee2e6",
        backgroundColor: "#fff",
        "&:disabled": {
          opacity: 0.4,
          cursor: "not-allowed",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "#f1f3f5",
        },
      },
    },
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Roles List", 14, 10);
    const tableColumn = ["Name", "Permissions"];
    const tableRows = [];

    filteredRoles.forEach((role) => {
      const permissions =
        role.permissions && role.permissions.length > 0
          ? role.permissions.map((p) => p.name).join(", ")
          : "No permissions";
      tableRows.push([role.name, permissions]);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("roles_list.pdf");
  };

  const handleCopy = () => {
    const copyText = filteredRoles
      .map(
        (r) => `${r.name}\t${r.permissions.map((p) => p.name).join(", ")}`
      )
      .join("\n");

    navigator.clipboard.writeText(copyText).then(() => {
      Swal.fire({
        title: "Copied!",
        text: "Roles and permissions have been copied to clipboard.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
        position: "top",
        width: "280px",
      });
    });
  };

  const handlePrint = () => {
    const printContent = `
      <html>
      <head>
        <title>Roles List</title>
        <style>
          body { font-family: Arial; margin: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
          th { background: #f8f9fa; }
        </style>
      </head>
      <body>
        <h3>Roles List</h3>
        <table>
          <thead>
            <tr><th>Name</th><th>Permissions</th></tr>
          </thead>
          <tbody>
            ${filteredRoles
        .map(
          (r) => `
              <tr>
                <td>${r.name}</td>
                <td>${r.permissions.map((p) => p.name).join(", ")}</td>
              </tr>`
        )
        .join("")}
          </tbody>
        </table>
      </body>
      </html>`;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const csvData = filteredRoles.map((role) => ({
    Name: role.name,
    Permissions: role.permissions
      ? role.permissions.map((p) => p.name).join(", ")
      : "No permissions",
  }));

  // const showConfirm = (roleId) => {
  //   Swal.fire({
  //     title: "Are you sure?",
  //     text: "You won't be able to revert this!",
  //     icon: "warning",
  //     position: "top",
  //     showCancelButton: true,
  //     confirmButtonColor: "#3085d6",
  //     cancelButtonColor: "#d33",
  //     confirmButtonText: "Yes, delete it!",
  //   }).then((result) => {
  //     if (result.isConfirmed) {

  //       Swal.fire({
  //         icon: "success",
  //         title: "Deleted!",
  //         text: "Role has been deleted successfully.",
  //         showConfirmButton: false,
  //         timer: 1500,
  //       });
  //     }
  //   });
  // };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "160px",
    },
    {
      name: "Permissions",
      cell: (row) => {
        const visibleCount = 7;
        const remainingCount = row.permissions.length - visibleCount;
        return (
          <div className="d-flex flex-wrap align-items-center gap-1">
            {row.permissions.slice(0, visibleCount).map((p) => (
              <span key={p.id} className="badge bg-primary">
                {p.name}
              </span>
            ))}
            {remainingCount > 0 && (
              <span
                className="badge bg-secondary cursor-pointer"
                onClick={() => setSelectedRole(row)}
                style={{ cursor: 'pointer' }}
              >
                +{remainingCount} More
              </span>
            )}
          </div>
        );
      },
      grow: 3,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {onEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit"
              onClick={() => onEdit(row)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      width: "150px",
    },
  ];

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown export-dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={csvData} // <--- Change this from filteredRoles to csvData
                  filename="roles_export.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleCopy}>
                  <i className="ri-file-copy-line me-1"></i> Copy
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handlePrint}>
                  <i className="ri-printer-line me-1"></i> Print
                </button>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search role..."
            className="form-control form-control-sm"
            style={{ width: "200px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} roles
        </h6>

        {/* Add Role */}
        <div>
          {onAddRole && (
            <button className="btn btn-primary btn-sm" onClick={onAddRole}>
              <i className="ri-shield-user-line me-1"></i> Add Role
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredRoles}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          customStyles={customStyles}
          noDataComponent={<div className="p-3 text-muted">No roles found</div>}
        />
      </div>

      {/* Permissions Modal */}
      <Modal
        show={!!selectedRole}
        onHide={() => setSelectedRole(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Permissions for {selectedRole?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRole?.permissions?.length > 0 ? (
            selectedRole.permissions.map((perm) => (
              <span key={perm.id} className="badge bg-primary me-1 mb-1">
                {perm.name}
              </span>
            ))
          ) : (
            <div className="text-muted">No permissions assigned</div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RolesList;